import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Grid,
  Autocomplete,
  IconButton,
  Typography,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Article } from '../types';
import { Supplier } from '../types/supplier';
import { StockEntryNew, StockEntryNewUpdate, StockEntryItemCreate } from '../types/stockEntryNew';
import { stockEntriesNewAPI } from '../services/api';
import { extractErrorMessage } from '../utils/errorHandler';
import ConfirmationDialog, { ConfirmationField } from './ConfirmationDialog';

interface StockEntryNewEditProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  stockEntry: StockEntryNew | null;
  articles: Article[];
  suppliers: Supplier[];
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
}

interface ArticleLineItem extends Omit<StockEntryItemCreate, 'unit_price' | 'quantity'> {
  article?: Article;
  quantity: number | null | string;
  unit_price: number | null | string;
}

const StockEntryNewEdit: React.FC<StockEntryNewEditProps> = ({
  open,
  onClose,
  onSuccess,
  stockEntry,
  articles,
  suppliers,
  showError,
  showSuccess,
}) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    bl_number: '',
    supplier_id: null as number | null,
  });

  const [articleLines, setArticleLines] = useState<ArticleLineItem[]>([
    {
      article_id: 0,
      quantity: null,
      unit_price: null,
    },
  ]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load stock entry data when dialog opens
  useEffect(() => {
    if (open && stockEntry) {
      setFormData({
        date: stockEntry.date,
        bl_number: stockEntry.bl_number,
        supplier_id: stockEntry.supplier_id,
      });

      // Load items
      if (stockEntry.items && stockEntry.items.length > 0) {
        setArticleLines(
          stockEntry.items.map((item) => ({
            article_id: item.article_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
            article: articles.find((a) => a.id === item.article_id),
          }))
        );
      } else {
        setArticleLines([
          {
            article_id: 0,
            quantity: null,
            unit_price: null,
          },
        ]);
      }
    }
  }, [open, stockEntry, articles]);

  const formatNumber = (value: any, decimals: number = 2): string => {
    return Number(value || 0).toFixed(decimals);
  };

  const handleAddArticleLine = () => {
    setArticleLines([
      ...articleLines,
      {
        article_id: 0,
        quantity: null,
        unit_price: null,
      },
    ]);
  };

  const handleRemoveArticleLine = (index: number) => {
    if (articleLines.length > 1) {
      setArticleLines(articleLines.filter((_, i) => i !== index));
    }
  };

  const handleArticleChange = (index: number, article: Article | null) => {
    const newLines = [...articleLines];

    if (article) {
      newLines[index] = {
        ...newLines[index],
        article_id: article.id,
        article: article,
      };
    } else {
      newLines[index] = {
        article_id: 0,
        quantity: null,
        unit_price: null,
      };
    }

    setArticleLines(newLines);
  };

  const handleArticleFieldChange = (
    index: number,
    field: keyof ArticleLineItem,
    value: any
  ) => {
    const newLines = [...articleLines];
    newLines[index] = {
      ...newLines[index],
      [field]: value,
    };
    setArticleLines(newLines);
  };

  const calculateGrandTotal = (): number => {
    return articleLines.reduce((total, line) => {
      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.')) || 0
        : (line.quantity ?? 0);
      const price = typeof line.unit_price === 'string'
        ? parseFloat(line.unit_price.replace(',', '.')) || 0
        : (line.unit_price ?? 0);
      const lineTotal = quantity * price;
      return total + lineTotal;
    }, 0);
  };

  const handleSubmitClick = () => {
    // Validate all required fields
    if (!formData.bl_number || !formData.bl_number.trim()) {
      showError('Veuillez saisir le numero de BL');
      return;
    }

    if (!formData.supplier_id) {
      showError('Veuillez selectionner un fournisseur');
      return;
    }

    // Validate article lines
    for (let i = 0; i < articleLines.length; i++) {
      const line = articleLines[i];

      if (!line.article_id || line.article_id === 0) {
        showError(`Veuillez selectionner un article pour la ligne ${i + 1}`);
        return;
      }

      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.'))
        : line.quantity;

      if (quantity === null || isNaN(quantity as number) || (quantity as number) <= 0) {
        showError(`La quantite doit etre superieure a 0 pour la ligne ${i + 1}`);
        return;
      }

      const price = typeof line.unit_price === 'string'
        ? parseFloat(line.unit_price.replace(',', '.'))
        : line.unit_price;

      if (price === null || isNaN(price as number) || (price as number) < 0) {
        showError(`Le prix unitaire doit etre superieur ou egal a 0 pour la ligne ${i + 1}`);
        return;
      }
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    if (!stockEntry) return;

    setIsSubmitting(true);

    try {
      const updateData: StockEntryNewUpdate = {
        date: formData.date || new Date().toISOString().split('T')[0],
        bl_number: formData.bl_number,
        supplier_id: formData.supplier_id!,
        items: articleLines.map((line) => {
          const quantity = typeof line.quantity === 'string'
            ? parseFloat(line.quantity.replace(',', '.'))
            : (line.quantity ?? 0);
          const price = typeof line.unit_price === 'string'
            ? parseFloat(line.unit_price.replace(',', '.'))
            : (line.unit_price ?? 0);
          return {
            article_id: line.article_id,
            quantity: Math.round(quantity * 100) / 100,
            unit_price: Math.round(price * 100) / 100,
          };
        }),
      };

      await stockEntriesNewAPI.update(stockEntry.id!, updateData);
      setShowConfirmDialog(false);
      showSuccess('Entree de stock mise a jour avec succes!');
      onSuccess();
      handleClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getConfirmationFields = (): ConfirmationField[] => {
    const fields: ConfirmationField[] = [];

    fields.push({ label: 'Date', value: formData.date });
    fields.push({ label: 'BL N', value: formData.bl_number });

    if (formData.supplier_id) {
      const supplier = suppliers.find((s) => s.id === formData.supplier_id);
      if (supplier) {
        fields.push({ label: 'Fournisseur', value: `${supplier.code} - ${supplier.name}` });
      }
    }

    fields.push({ label: '---', value: '---' });
    fields.push({ label: 'Articles', value: `${articleLines.length}`, bold: true });

    articleLines.forEach((line, index) => {
      const article = articles.find((a) => a.id === line.article_id);
      if (article) {
        const quantity = typeof line.quantity === 'string'
          ? parseFloat(line.quantity.replace(',', '.')) || 0
          : (line.quantity ?? 0);
        const price = typeof line.unit_price === 'string'
          ? parseFloat(line.unit_price.replace(',', '.')) || 0
          : (line.unit_price ?? 0);

        fields.push({
          label: `Article ${index + 1}`,
          value: `${article.reference} - ${article.designation}`,
        });
        fields.push({
          label: `  Quantite`,
          value: `${quantity}`,
        });
        fields.push({
          label: `  Prix Unitaire`,
          value: `${formatNumber(price)} MAD`,
        });
        fields.push({
          label: `  Total`,
          value: `${formatNumber(quantity * price)} MAD`,
        });
      }
    });

    fields.push({ label: '---', value: '---' });
    fields.push({
      label: 'TOTAL GENERAL',
      value: `${formatNumber(calculateGrandTotal())} MAD`,
      bold: true,
    });

    return fields;
  };

  const handleClose = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      bl_number: '',
      supplier_id: null,
    });
    setArticleLines([
      {
        article_id: 0,
        quantity: null,
        unit_price: null,
      },
    ]);
    setShowConfirmDialog(false);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Modifier l'entree de stock</DialogTitle>
        <DialogContent>
          {/* Date and BL Number */}
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="BL N"
                  value={formData.bl_number}
                  onChange={(e) => setFormData({ ...formData, bl_number: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Article Lines Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Articles</Typography>
              <IconButton
                color="primary"
                size="small"
                onClick={handleAddArticleLine}
                sx={{ border: '2px solid', borderColor: 'primary.main' }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            {articleLines.map((line, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={articles}
                      getOptionLabel={(option) => `${option.reference} - ${option.designation}`}
                      value={articles.find((a) => a.id === line.article_id) || null}
                      onChange={(_, value) => handleArticleChange(index, value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Article"
                          placeholder="Selectionner article"
                          size="small"
                          required
                        />
                      )}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={3}>
                    <TextField
                      label="Quantite"
                      type="text"
                      value={line.quantity ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'quantity', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'quantity', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'quantity', numValue);
                          } else {
                            handleArticleFieldChange(index, 'quantity', null);
                          }
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={3}>
                    <TextField
                      label="Prix Unitaire"
                      type="text"
                      value={line.unit_price ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'unit_price', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'unit_price', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'unit_price', numValue);
                          } else {
                            handleArticleFieldChange(index, 'unit_price', null);
                          }
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm="auto" md="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveArticleLine(index)}
                      disabled={articleLines.length === 1}
                      sx={{
                        border: '1px solid',
                        borderColor: articleLines.length === 1 ? 'grey.300' : 'error.main',
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Supplier Field */}
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={suppliers}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  value={suppliers.find((s) => s.id === formData.supplier_id) || null}
                  onChange={(_, value) => setFormData({ ...formData, supplier_id: value?.id || null })}
                  renderInput={(params) => <TextField {...params} label="Fournisseur" required />}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSubmitClick} variant="contained">
            Mettre a jour
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirmDialog}
        title="Confirmer la modification"
        message="Veuillez verifier les informations avant de confirmer la modification."
        fields={getConfirmationFields()}
        movementType="entry"
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmDialog(false)}
        confirmText="Confirmer"
        cancelText="Annuler"
        loading={isSubmitting}
      />
    </>
  );
};

export default StockEntryNewEdit;
